import React, { useMemo } from "react";
import { StyledFieldLabelLight } from "@iventis/styles";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { convertLengthFeetToMeters, convertLengthMetersToFeet } from "@iventis/utilities";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { IncrementalValueComponent } from "./selectors/incremental-value-input";

const MAX_VALUE = 100000;
const MIN_VALUE = 0.01;

export const HeightWidthField: React.FunctionComponent<{
    values: [number, number];
    valueChanged: (value: number, axis: "x" | "y") => void;
    unitOfMeasurement: UnitOfMeasurement;
    disabled?: boolean;
}> = ({ values, valueChanged, disabled = false, unitOfMeasurement }) => {
    const translate = useIventisTranslate();

    const { maxHeightWidthValue, minHeightWidthValue } = useMemo(() => {
        if (unitOfMeasurement === UnitOfMeasurement.Imperial) {
            return { maxHeightWidthValue: convertLengthMetersToFeet(MAX_VALUE), minHeightWidthValue: convertLengthMetersToFeet(MIN_VALUE) };
        }
        return { maxHeightWidthValue: MAX_VALUE, minHeightWidthValue: MIN_VALUE };
    }, [unitOfMeasurement]);

    const convertedValues = useMemo(() => {
        if (unitOfMeasurement === UnitOfMeasurement.Imperial) {
            return [convertLengthMetersToFeet(values[0]), convertLengthMetersToFeet(values[1])];
        }
        return values;
    }, [unitOfMeasurement, values[0], values[1]]);

    const handleValueChanged = (value: number, coordinate: "x" | "y") => {
        if (unitOfMeasurement === UnitOfMeasurement.Imperial) {
            valueChanged(convertLengthFeetToMeters(value), coordinate);
        } else {
            valueChanged(value, coordinate);
        }
    };

    return (
        <div
            data-testid="height-width-field"
            className="input-margin-bottom"
            style={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between", gap: "10px" }}
        >
            <div style={{ display: "flex", flexDirection: "column" }}>
                <StyledFieldLabelLight>
                    <span>{translate(Content.map5.fixed_shape.width)}</span> (
                    {unitOfMeasurement === UnitOfMeasurement.Metric ? <span>{translate(Content.map3.units.meters)}</span> : <span>{translate(Content.map5.units.feet)}</span>})
                </StyledFieldLabelLight>
                <IncrementalValueComponent
                    disabled={disabled}
                    minValue={minHeightWidthValue}
                    maxValue={maxHeightWidthValue}
                    increment={0.01}
                    decimals={2}
                    value={convertedValues[0]}
                    changeValue={(value) => handleValueChanged(value, "x")}
                    fullWidth
                    hideError
                    changeToValidValueOnUnfocus
                    dataCy="width-input"
                />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <StyledFieldLabelLight>
                    <span>{translate(Content.map5.fixed_shape.length)}</span> (
                    {unitOfMeasurement === UnitOfMeasurement.Metric ? <span>{translate(Content.map3.units.meters)}</span> : <span>{translate(Content.map5.units.feet)}</span>})
                </StyledFieldLabelLight>
                <IncrementalValueComponent
                    disabled={disabled}
                    minValue={minHeightWidthValue}
                    maxValue={maxHeightWidthValue}
                    increment={0.01}
                    decimals={2}
                    value={convertedValues[1]}
                    changeValue={(value) => handleValueChanged(value, "y")}
                    fullWidth
                    hideError
                    changeToValidValueOnUnfocus
                    dataCy="length-input"
                />
            </div>
        </div>
    );
};
